<template>
  <HrPageLayout
    title="Performance Improvement Plans"
    subtitle="Create PIPs, track weekly sign-offs, and close plans when objectives are met"
    icon="bx bx-task"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'PIP' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreateModal">
        <i class="bx bx-plus"></i> Create PIP
      </button>
    </template>

    <div class="pip-page">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-list-ul"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total PIPs</p>
          <h2 class="hr-metric-value">{{ metrics.total }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Active</p>
          <h2 class="hr-metric-value">{{ metrics.active }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-check-double"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Completed</p>
          <h2 class="hr-metric-value">{{ metrics.completed }}</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="pip-filters">
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filterStatus" class="hr-select" @change="loadPips">
                <option value="">All statuses</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Terminated">Terminated</option>
                <option value="Withdrawn">Withdrawn</option>
              </select>
            </div>
            <div class="hr-filter-group pip-search">
              <label class="hr-form-label">Search</label>
              <input
                v-model="searchQuery"
                type="search"
                class="hr-input"
                placeholder="Employee or manager name..."
                @input="debouncedLoad"
              />
            </div>
            <button type="button" class="hr-btn hr-btn-outline hr-btn-secondary" @click="resetFilters">
              <i class="bx bx-refresh"></i> Reset
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="hr-loading-wrap hr-py-5">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted hr-mt-2">Loading PIPs...</p>
      </div>

      <div v-else-if="!pips.length" class="hr-chart-card hr-text-center hr-py-5">
        <i class="bx bx-clipboard" style="font-size: 48px; color: #94a3b8;"></i>
        <h5 class="hr-mt-4">No PIPs found</h5>
        <p class="hr-text-muted">Create a plan for an employee to start weekly progress sign-offs.</p>
        <button type="button" class="hr-btn hr-btn-primary hr-mt-3" @click="openCreateModal">
          <i class="bx bx-plus"></i> Create PIP
        </button>
      </div>

      <div v-else class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Manager</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th class="hr-text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in pips" :key="p.id">
                  <td>
                    <div class="pip-emp-name">{{ p.employee_name }}</div>
                    <div class="hr-text-muted hr-small">{{ p.employee_emp_no }}</div>
                  </td>
                  <td>{{ p.manager_name || '—' }}</td>
                  <td>{{ formatDate(p.start_date) }} – {{ formatDate(p.end_date) }}</td>
                  <td>
                    <span class="hr-badge" :class="statusClass(p.status)">{{ p.status }}</span>
                  </td>
                  <td class="hr-text-end">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="viewPip(p)">
                      <i class="bx bx-show"></i> Open
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create PIP -->
    <ModalWrapper ref="createModal" title="Create Performance Improvement Plan" size="large">
      <template #body>
        <div class="pip-form">
          <div class="pip-employee-picker">
            <div class="hr-form-label">Employee <span class="pip-required">*</span></div>
            <input
              v-model="employeeSearch"
              type="text"
              class="hr-input"
              placeholder="Search by name, employee no, or ID..."
              autocomplete="off"
              @input="debouncedEmployeeSearch"
              @focus="onEmployeeSearchFocus"
            />
            <p v-if="employeeSearchLoading" class="pip-search-hint hr-mt-1">
              <span class="hr-spinner small"></span> Searching...
            </p>
            <p v-else-if="employeeSearch && !employeeResults.length" class="pip-search-hint hr-mt-1">
              No employees match your search.
            </p>
            <p v-else-if="!employeeSearch && !employeeResults.length && !createForm.employeeId" class="pip-search-hint hr-mt-1">
              Type to search, or pick from the list below.
            </p>
            <div
              v-if="employeeResults.length && !createForm.employeeId"
              class="pip-search-results"
              role="listbox"
            >
              <button
                v-for="emp in employeeResults"
                :key="emp.id"
                type="button"
                class="pip-search-item"
                role="option"
                @mousedown.prevent
                @click="selectEmployee(emp)"
              >
                <strong>{{ employeeDisplayName(emp) }}</strong>
                <span>{{ emp.empNo || emp.nationalId }} · {{ emp.departmentName || 'No department' }}</span>
              </button>
            </div>
          </div>
          <div v-if="createForm.employeeId" class="pip-selected-card hr-mt-2">
            <div class="pip-selected-row">
              <div>
                <div><strong>{{ createForm.employeeName }}</strong></div>
                <div class="hr-text-muted hr-small">{{ createForm.employeeEmpNo }} · Manager: {{ createForm.managerName || 'from reportingTo' }}</div>
              </div>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline hr-btn-secondary" @click="clearSelectedEmployee">
                Change
              </button>
            </div>
          </div>

          <div class="pip-form-grid hr-mt-3">
            <div class="hr-filter-group">
              <label class="hr-form-label">Start date <span class="pip-required">*</span></label>
              <input v-model="createForm.startDate" type="date" class="hr-input" />
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">End date <span class="pip-required">*</span></label>
              <input v-model="createForm.endDate" type="date" class="hr-input" />
            </div>
          </div>
          <div class="hr-filter-group hr-mt-2">
            <label class="hr-form-label">Goals & improvement plan</label>
            <textarea
              v-model="createForm.reason"
              class="hr-textarea"
              rows="4"
              placeholder="Specific objectives, support, and success criteria"
            ></textarea>
          </div>
          <div class="hr-filter-group hr-mt-2">
            <label class="hr-form-label">Manager override (optional)</label>
            <div class="pip-employee-picker">
              <input
                v-model="managerSearch"
                type="text"
                class="hr-input"
                placeholder="Leave blank to use employee's reportingTo supervisor"
                autocomplete="off"
                @input="debouncedManagerSearch"
                @focus="loadManagerOptions"
              />
              <div v-if="managerResults.length" class="pip-search-results">
                <button
                  v-for="mgr in managerResults"
                  :key="mgr.id || mgr.nationalId"
                  type="button"
                  class="pip-search-item"
                  @mousedown.prevent
                  @click="selectManager(mgr)"
                >
                  <strong>{{ employeeDisplayName(mgr) }}</strong>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.createModal.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="creating" @click="createPip">
          <span v-if="creating" class="hr-spinner small"></span>
          {{ creating ? 'Creating...' : 'Create PIP' }}
        </button>
      </template>
    </ModalWrapper>

    <!-- View PIP -->
    <ModalWrapper ref="viewModal" :title="viewTitle" size="large">
      <template #body>
        <div v-if="loadingDetail" class="hr-loading-wrap hr-py-4">
          <div class="hr-spinner"></div>
        </div>
        <div v-else-if="selectedPip" class="pip-detail">
          <div class="pip-detail-header">
            <div>
              <p class="hr-text-muted hr-small hr-mb-1">Period</p>
              <p class="pip-period">
                {{ formatDate(selectedPip.start_date) }} – {{ formatDate(selectedPip.end_date) }}
              </p>
            </div>
            <span class="hr-badge" :class="statusClass(selectedPip.status)">{{ selectedPip.status }}</span>
          </div>
          <div v-if="selectedPip.reason || selectedPip.goals" class="pip-goals-box hr-mt-3">
            <p class="hr-form-label">Improvement plan</p>
            <p class="pip-goals-text">{{ selectedPip.reason || selectedPip.goals }}</p>
          </div>

          <h4 class="pip-section-title hr-mt-4">Weekly sign-offs</h4>
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-compact">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Progress</th>
                  <th>Employee</th>
                  <th>Manager</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in signoffWeeks" :key="weekKey(s)">
                  <td>{{ formatDate(weekStart(s)) }} – {{ formatDate(weekEnd(s)) }}</td>
                  <td class="pip-progress-cell">{{ truncate(s.progress_notes) }}</td>
                  <td>
                    <span v-if="s.employee_signed_at" class="pip-signed">
                      <i class="bx bx-check-circle"></i> {{ formatDateTime(s.employee_signed_at) }}
                    </span>
                    <span v-else class="hr-text-muted">Pending</span>
                  </td>
                  <td>
                    <span v-if="s.manager_signed_at" class="pip-signed">
                      <i class="bx bx-check-circle"></i> {{ formatDateTime(s.manager_signed_at) }}
                    </span>
                    <span v-else class="hr-text-muted">Pending</span>
                  </td>
                  <td class="hr-text-end">
                    <button
                      v-if="selectedPip.status === 'Active'"
                      type="button"
                      class="hr-btn hr-btn-sm hr-btn-outline hr-btn-primary"
                      @click="openSignoffModal(s)"
                    >
                      {{ signoffButtonLabel(s) }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="selectedPip.status === 'Active'" class="pip-detail-actions hr-mt-4">
            <button type="button" class="hr-btn hr-btn-success" @click="updatePipStatus('Completed')">
              Mark completed
            </button>
            <button type="button" class="hr-btn hr-btn-danger" @click="updatePipStatus('Terminated')">
              Terminate
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.viewModal.close()">Close</button>
      </template>
    </ModalWrapper>

    <!-- Sign-off -->
    <ModalWrapper ref="signoffModal" :title="signoffModalTitle" size="medium">
      <template #body>
        <div class="hr-filter-group">
          <label class="hr-form-label">Progress notes</label>
          <textarea v-model="signoffForm.progressNotes" class="hr-textarea" rows="3"></textarea>
        </div>
        <div class="hr-filter-group hr-mt-2">
          <label class="hr-form-label">Comments ({{ signoffAs === 'manager' ? 'Manager' : 'Employee' }})</label>
          <textarea v-model="signoffForm.comments" class="hr-textarea" rows="2"></textarea>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.signoffModal.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="signing" @click="submitSignoff">
          <span v-if="signing" class="hr-spinner small"></span>
          Sign week
        </button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute, toast } from "@/api";
import HrPageLayout from "./HrPageLayout.vue";
import ModalWrapper from "./ModalWrapper.vue";

export default {
  name: "PipManagement",
  components: { HrPageLayout, ModalWrapper },
  data() {
    const today = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 90);
    return {
      loading: false,
      loadingDetail: false,
      creating: false,
      signing: false,
      filterStatus: "",
      searchQuery: "",
      searchTimer: null,
      employeeSearchTimer: null,
      managerSearchTimer: null,
      employeeSearchLoading: false,
      pips: [],
      selectedPip: null,
      signoffWeeks: [],
      currentUserNationalId: "",
      employeeSearch: "",
      employeeResults: [],
      managerSearch: "",
      managerResults: [],
      createForm: {
        employeeId: "",
        employeeEmpNo: "",
        employeeNationalId: "",
        employeeName: "",
        managerEmpNo: "",
        managerNationalId: "",
        managerName: "",
        startDate: today.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
        reason: ""
      },
      signoffForm: {
        week_start: "",
        week_end: "",
        progressNotes: "",
        comments: ""
      },
      signoffAs: "manager"
    };
  },
  computed: {
    metrics() {
      const all = this.pips;
      return {
        total: all.length,
        active: all.filter((p) => p.status === "Active").length,
        completed: all.filter((p) => p.status === "Completed").length
      };
    },
    viewTitle() {
      return this.selectedPip
        ? `PIP — ${this.selectedPip.employee_name}`
        : "PIP details";
    },
    signoffModalTitle() {
      return this.signoffForm.week_start
        ? `Sign-off · week of ${this.formatDate(this.signoffForm.week_start)}`
        : "Weekly sign-off";
    }
  },
  mounted() {
    this.loadPips();
    this.loadCurrentUser();
  },
  methods: {
    debouncedLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadPips(), 350);
    },
    resetFilters() {
      this.filterStatus = "";
      this.searchQuery = "";
      this.loadPips();
    },
    async loadCurrentUser() {
      try {
        const data = new FormData();
        data.append("function", "getEmployeeInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data) {
          this.currentUserNationalId = res.data.data.nationalId || "";
        }
      } catch (e) {
        console.error(e);
      }
    },
    async loadPips() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getPips");
        if (this.filterStatus) data.append("status", this.filterStatus);
        if (this.searchQuery) data.append("search", this.searchQuery);
        const res = await execute(data, authUrl);
        this.pips = res?.data?.success ? (res.data.data?.pips ?? []) : [];
      } catch (e) {
        toast.error("Failed to load PIPs");
        this.pips = [];
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      const today = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 90);
      this.createForm = {
        employeeId: "",
        employeeEmpNo: "",
        employeeNationalId: "",
        employeeName: "",
        managerEmpNo: "",
        managerNationalId: "",
        managerName: "",
        startDate: today.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
        reason: ""
      };
      this.employeeSearch = "";
      this.managerSearch = "";
      this.employeeResults = [];
      this.managerResults = [];
      this.$refs.createModal.open();
      this.loadEmployeeOptions();
    },
    employeeDisplayName(emp) {
      if (!emp) return "";
      if (emp.fullName) return emp.fullName;
      return `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.empNo || "Employee";
    },
    debouncedEmployeeSearch() {
      if (this.createForm.employeeId) {
        this.createForm.employeeId = "";
        this.createForm.employeeEmpNo = "";
        this.createForm.employeeNationalId = "";
        this.createForm.employeeName = "";
      }
      clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => this.loadEmployeeOptions(), 300);
    },
    debouncedManagerSearch() {
      clearTimeout(this.managerSearchTimer);
      this.managerSearchTimer = setTimeout(() => this.loadManagerOptions(), 300);
    },
    onEmployeeSearchFocus() {
      if (!this.employeeResults.length) {
        this.loadEmployeeOptions();
      }
    },
    async loadEmployeeOptions() {
      this.employeeSearchLoading = true;
      try {
        const data = new FormData();
        data.append("function", "searchEmployeesForPip");
        data.append("q", (this.employeeSearch || "").trim());
        data.append("limit", "30");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.employeeResults = res.data.data?.employees ?? [];
        } else {
          this.employeeResults = await this.fetchEmployeesViaGetEmployees();
        }
      } catch (e) {
        this.employeeResults = await this.fetchEmployeesViaGetEmployees();
      } finally {
        this.employeeSearchLoading = false;
      }
    },
    async fetchEmployeesViaGetEmployees() {
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", "1");
        data.append("rows_per_page", "30");
        data.append("status", "Active");
        if ((this.employeeSearch || "").trim()) {
          data.append("keyword", this.employeeSearch.trim());
        }
        const res = await execute(data, authUrl);
        if (!res?.data?.success) return [];
        return (res.data.data?.employees ?? []).map((e) => ({
          id: e.id,
          empNo: e.empNo,
          nationalId: e.nationalId,
          fullName: this.employeeDisplayName(e),
          firstName: e.firstName,
          lastName: e.lastName,
          positionTitle: e.positionTitle,
          departmentName: e.departmentName,
          reportingTo: e.reportingTo,
          email: e.email
        }));
      } catch (e) {
        return [];
      }
    },
    async loadManagerOptions() {
      if ((this.managerSearch || "").trim().length < 1) {
        this.managerResults = [];
        return;
      }
      const data = new FormData();
      data.append("function", "searchEmployeesForPip");
      data.append("q", this.managerSearch.trim());
      data.append("limit", "20");
      const res = await execute(data, authUrl);
      this.managerResults = res?.data?.success ? (res.data.data?.employees ?? []) : [];
    },
    async selectEmployee(emp) {
      this.createForm.employeeId = emp.id;
      this.createForm.employeeEmpNo = emp.empNo;
      this.createForm.employeeNationalId = emp.nationalId;
      this.createForm.employeeName = this.employeeDisplayName(emp);
      this.employeeSearch = this.createForm.employeeName;
      this.employeeResults = [];
      if (emp.reportingTo) {
        const mgr = await this.resolveEmployee(emp.reportingTo);
        if (mgr) {
          this.createForm.managerEmpNo = mgr.empNo;
          this.createForm.managerNationalId = mgr.nationalId;
          this.createForm.managerName = `${mgr.firstName} ${mgr.lastName}`.trim();
        }
      }
    },
    selectManager(mgr) {
      this.createForm.managerEmpNo = mgr.empNo;
      this.createForm.managerNationalId = mgr.nationalId;
      this.createForm.managerName = this.employeeDisplayName(mgr);
      this.managerSearch = this.createForm.managerName;
      this.managerResults = [];
    },
    clearSelectedEmployee() {
      this.createForm.employeeId = "";
      this.createForm.employeeEmpNo = "";
      this.createForm.employeeNationalId = "";
      this.createForm.employeeName = "";
      this.employeeSearch = "";
      this.loadEmployeeOptions();
    },
    async resolveEmployee(identifier) {
      const data = new FormData();
      data.append("function", "resolveEmployeeIdentifier");
      data.append("identifier", identifier);
      const res = await execute(data, authUrl);
      return res?.data?.success ? res.data.data : null;
    },
    async createPip() {
      if (!this.createForm.employeeId || !this.createForm.startDate || !this.createForm.endDate) {
        toast.warning("Select an employee and set the plan dates.");
        return;
      }
      this.creating = true;
      try {
        const data = new FormData();
        data.append("function", "createPip");
        data.append("employeeId", this.createForm.employeeId);
        data.append("employeeEmpNo", this.createForm.employeeEmpNo);
        data.append("employeeNationalId", this.createForm.employeeNationalId);
        data.append("employeeName", this.createForm.employeeName);
        if (this.createForm.managerNationalId) {
          data.append("managerEmpNo", this.createForm.managerEmpNo);
          data.append("managerNationalId", this.createForm.managerNationalId);
          data.append("managerName", this.createForm.managerName);
        }
        data.append("startDate", this.createForm.startDate);
        data.append("endDate", this.createForm.endDate);
        data.append("reason", this.createForm.reason || "");
        data.append("goals", this.createForm.reason || "");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          toast.success("PIP created successfully");
          this.$refs.createModal.close();
          this.loadPips();
        } else {
          throw new Error(res?.data?.message || "Failed to create PIP");
        }
      } catch (e) {
        toast.error(e.message || "Failed to create PIP");
      } finally {
        this.creating = false;
      }
    },
    async viewPip(p) {
      this.selectedPip = p;
      this.loadingDetail = true;
      this.$refs.viewModal.open();
      try {
        const data = new FormData();
        data.append("function", "getPipDetail");
        data.append("id", p.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.selectedPip = res.data.data?.pip ?? p;
          this.signoffWeeks = res.data.data?.signoffs ?? [];
        }
      } catch (e) {
        toast.error("Failed to load PIP details");
      } finally {
        this.loadingDetail = false;
      }
    },
    weekKey(s) {
      return this.weekStart(s);
    },
    weekStart(s) {
      return s.week_start_date || s.week_start;
    },
    weekEnd(s) {
      return s.week_end_date || s.week_end;
    },
    signoffButtonLabel(s) {
      if (s.status === "Both_Signed") return "View";
      const isManager =
        this.selectedPip?.manager_national_id === this.currentUserNationalId;
      if (isManager && s.employee_signed_at && !s.manager_signed_at) return "Manager sign";
      if (!isManager && !s.employee_signed_at) return "Sign";
      return "Update";
    },
    openSignoffModal(s) {
      const isManager =
        this.selectedPip?.manager_national_id === this.currentUserNationalId;
      this.signoffAs = isManager ? "manager" : "employee";
      this.signoffForm = {
        week_start: this.weekStart(s),
        week_end: this.weekEnd(s),
        progressNotes: s.progress_notes || "",
        comments: isManager ? (s.manager_comments || "") : (s.employee_comments || "")
      };
      this.$refs.signoffModal.open();
    },
    async submitSignoff() {
      this.signing = true;
      try {
        const data = new FormData();
        data.append("function", "addOrUpdatePipWeeklySignoff");
        data.append("pipId", this.selectedPip.id);
        data.append("weekStartDate", this.signoffForm.week_start);
        data.append("progressNotes", this.signoffForm.progressNotes || "");
        data.append("signedAs", this.signoffAs);
        if (this.signoffAs === "employee") {
          data.append("employeeComments", this.signoffForm.comments || "");
        } else {
          data.append("managerComments", this.signoffForm.comments || "");
        }
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          toast.success("Sign-off saved");
          this.$refs.signoffModal.close();
          await this.viewPip(this.selectedPip);
        } else {
          throw new Error(res?.data?.message || "Failed to save sign-off");
        }
      } catch (e) {
        toast.error(e.message || "Failed to save sign-off");
      } finally {
        this.signing = false;
      }
    },
    async updatePipStatus(status) {
      if (!confirm(`Change PIP status to "${status}"?`)) return;
      try {
        const data = new FormData();
        data.append("function", "updatePipStatus");
        data.append("pipId", this.selectedPip.id);
        data.append("status", status);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          toast.success("Status updated");
          this.$refs.viewModal.close();
          this.loadPips();
        } else {
          throw new Error(res?.data?.message || "Update failed");
        }
      } catch (e) {
        toast.error(e.message);
      }
    },
    formatDate(d) {
      if (!d) return "—";
      const date = new Date(d);
      return Number.isNaN(date.getTime())
        ? d
        : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    formatDateTime(d) {
      if (!d) return "—";
      const date = new Date(d);
      return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    truncate(text) {
      const t = (text || "").trim();
      if (!t) return "—";
      return t.length > 48 ? `${t.slice(0, 48)}…` : t;
    },
    statusClass(status) {
      const map = {
        Active: "hr-badge-warning",
        Completed: "hr-badge-success",
        Terminated: "hr-badge-danger",
        Withdrawn: "hr-badge-secondary"
      };
      return map[status] || "hr-badge-secondary";
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.pip-page {
  width: 100%;
}

.pip-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.pip-search {
  flex: 1;
  min-width: 200px;
}

.pip-emp-name {
  font-weight: 600;
}

.pip-required {
  color: #dc2626;
}

.pip-form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.pip-employee-picker {
  position: relative;
  z-index: 2;
}

.pip-search-hint {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.pip-search-results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin-top: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 2100;
}

.pip-search-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.pip-search-item:hover {
  background: #f8fafc;
}

.pip-search-item span {
  font-size: 12px;
  color: #64748b;
}

.pip-selected-card {
  padding: 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
}

.pip-selected-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pip-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.pip-period {
  font-weight: 600;
  margin: 0;
}

.pip-goals-box {
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.pip-goals-text {
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.5;
}

.pip-section-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 8px;
}

.hr-table-compact td,
.hr-table-compact th {
  font-size: 13px;
  padding: 10px 12px;
}

.pip-progress-cell {
  max-width: 180px;
}

.pip-signed {
  color: #059669;
  font-size: 12px;
}

.pip-detail-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.hr-text-end {
  text-align: right;
}

.hr-small {
  font-size: 12px;
}

@media (max-width: 768px) {
  .pip-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
